import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader, Card, DifficultyBadge, useCategoryLabel, CATEGORY_LABELS } from "@/components/common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ArrowLeft, RefreshCw, Lightbulb, ChevronRight } from "lucide-react";

export default function MCQTraining() {
  const { t, lang } = useLang();
  const catLabel = useCategoryLabel();
  const [scenarios, setScenarios] = useState([]);
  
  // State for active quiz
  const [activeCategoryScenarios, setActiveCategoryScenarios] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [scoreCount, setScoreCount] = useState(0);

  useEffect(() => { api.get("/scenarios").then((r) => setScenarios(r.data)); }, []);

  const categories = Array.from(new Set(scenarios.map((s) => s.category_code)));
  
  const uniqueCategoryCards = categories.map(catCode => {
    const list = scenarios.filter(s => s.category_code === catCode);
    return { ...list[0], count: list.length };
  });

  const startCategory = (catCode) => {
    const catsScenarios = scenarios.filter(s => s.category_code === catCode);
    setActiveCategoryScenarios(catsScenarios);
    setCurrentIndex(0);
    setSelected(null);
    setSubmitted(false);
    setScoreCount(0);
  };

  const nextQuestion = () => {
    if (currentIndex < activeCategoryScenarios.length - 1) {
      setCurrentIndex(p => p + 1);
      setSelected(null);
      setSubmitted(false);
    } else {
      setActiveCategoryScenarios(null);
    }
  };

  const submit = async () => {
    if (selected == null) return;
    setSubmitted(true);
    const active = activeCategoryScenarios[currentIndex];
    const correct = active.options[selected].correct;
    
    if (correct) {
      setScoreCount(p => p + 1);
    }
    
    const score = correct ? 1 : 0;
    const mistakes = correct ? [] : [`Wrong category for "${active.title_en}"`];
    await api.post("/sessions", {
      module: "mcq", scenario_id: active.scenario_id, category_code: active.category_code,
      score, max_score: 1, mistakes, details: { selected },
    });
    toast[correct ? "success" : "error"](correct ? t("correct") : t("incorrect"));
  };

  if (activeCategoryScenarios) {
    const active = activeCategoryScenarios[currentIndex];
    const correctIdx = active.options.findIndex((o) => o.correct);
    const isLast = currentIndex === activeCategoryScenarios.length - 1;
    
    return (
      <div className="max-w-3xl">
        <Button variant="ghost" onClick={() => setActiveCategoryScenarios(null)} className="mb-4" data-testid="mcq-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> {t("back")}
        </Button>
        <Card>
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-[#1e3a8a] bg-blue-50 border border-blue-200 rounded-full px-3 py-1 uppercase tracking-wider">
                {catLabel(active.category_code)}
              </span>
            </div>
            <div className="text-sm font-bold text-slate-500">
              Question {currentIndex + 1} of {activeCategoryScenarios.length}
            </div>
          </div>
          
          <h2 className="font-head text-xl font-bold text-slate-900 mb-3">{lang === "en" ? active.title_en : active.title_hi}</h2>
          <div className="rounded-md bg-slate-50 border border-slate-200 p-4 text-slate-700 mb-6 leading-relaxed">
            <div className="text-xs font-semibold text-slate-400 uppercase mb-1">{t("callScript")}</div>
            {lang === "en" ? active.script_en : active.script_hi}
          </div>
          <div className="font-medium text-slate-900 mb-3">{lang === "en" ? active.question_en : active.question_hi}</div>
          <div className="space-y-2.5">
            {active.options.map((o, i) => {
              let cls = "border-slate-300 hover:border-slate-400";
              if (submitted) {
                if (i === correctIdx) cls = "border-emerald-500 bg-emerald-50";
                else if (i === selected) cls = "border-red-500 bg-red-50";
                else cls = "border-slate-200 opacity-60";
              } else if (i === selected) cls = "border-blue-500 bg-blue-50";
              return (
                <button
                  key={i} data-testid={`mcq-option-${i}`} disabled={submitted}
                  onClick={() => setSelected(i)}
                  className={`w-full text-left rounded-md border-2 px-4 py-3 text-sm transition-colors flex items-center justify-between ${cls}`}
                >
                  <span className="text-slate-800">{lang === "en" ? o.en : o.hi}</span>
                  {submitted && i === correctIdx && <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />}
                  {submitted && i === selected && i !== correctIdx && <XCircle className="h-5 w-5 text-red-600 shrink-0" />}
                </button>
              );
            })}
          </div>

          {submitted && (
            <div className="mt-5 rounded-md bg-blue-50 border border-blue-200 p-4 animate-fade-in" data-testid="mcq-explanation">
              <div className="flex items-center gap-2 font-semibold text-blue-900 mb-1">
                <Lightbulb className="h-4 w-4" /> {t("explanation")}
              </div>
              <p className="text-sm text-blue-900/80">{lang === "en" ? active.explanation_en : active.explanation_hi}</p>
              {(active.do_ask_en || active.dont_ask_en) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  {active.do_ask_en && (
                    <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3" data-testid="mcq-do-ask">
                      <div className="text-xs font-semibold text-emerald-700 uppercase mb-1">{t("doAsk")}</div>
                      <ul className="list-disc pl-4 space-y-0.5 text-sm text-emerald-900/80">
                        {(lang === "en" ? active.do_ask_en : active.do_ask_hi).map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </div>
                  )}
                  {active.dont_ask_en && (
                    <div className="rounded-md bg-red-50 border border-red-200 p-3" data-testid="mcq-dont-ask">
                      <div className="text-xs font-semibold text-red-700 uppercase mb-1">{t("dontAsk")}</div>
                      <ul className="list-disc pl-4 space-y-0.5 text-sm text-red-900/80">
                        {(lang === "en" ? active.dont_ask_en : active.dont_ask_hi).map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex gap-3">
            {!submitted ? (
              <Button data-testid="mcq-submit" onClick={submit} disabled={selected == null} className="bg-[#0F172A] hover:bg-slate-800">
                {t("submit")}
              </Button>
            ) : (
              <Button data-testid="mcq-next" onClick={nextQuestion} className="bg-[#1e3a8a] hover:bg-blue-900 text-white">
                {isLast ? "Finish Quiz" : "Next Question"} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t("mcq")} subtitle="Select a category to start the scenario-based training module." />
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
        {uniqueCategoryCards.map((s) => (
          <Card key={s.category_code} className="hover:shadow-md transition-shadow flex flex-col cursor-pointer border-slate-200 hover:border-[#1e3a8a] bg-white group overflow-hidden" onClick={() => startCategory(s.category_code)}>
            <div className="h-2 w-full bg-[#1e3a8a]" />
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-1 rounded">{s.count} Scenarios</span>
                <DifficultyBadge level={s.difficulty} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-[#1e3a8a]">{catLabel(s.category_code)}</h3>
              <p className="text-sm text-slate-500 line-clamp-2 flex-1">Practice identifying and categorizing different real-world {catLabel(s.category_code).toLowerCase()} scenarios.</p>
              <div className="flex items-center text-sm font-bold text-[#1e3a8a] mt-4">
                START TRAINING <ChevronRight className="h-4 w-4 ml-1" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
